// Marcador "Foto do veículo" no mapa (pedido 2026-09-29) — escolhido POR
// VEÍCULO em Editar → Ícone no mapa (Vehicle.mapIconStyle === "photo",
// atributo wp.mapIcon no rastreador). Compartilhado entre o mapa desktop
// (routes/index.tsx#VehicleLayer) e o do app (mobile-app.tsx#
// MobileVehicleLayer). Ver lib/vehicle-icons/photo-marker.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVehicleImages } from "@/hooks/queries";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { AUTO_PREVIEW_COLOR, resolveAutoColor } from "@/lib/vehicle-icons";
import {
  ARROW_SCALE,
  headingArrowIcon,
  photoCircleIcon,
  photoFailed,
  PHOTO_MARKER_PX,
  PHOTO_MARKER_SIZE,
  photoMarkerSizeForZoom,
  placeholderCircleIcon,
} from "@/lib/vehicle-icons/photo-marker";
import type { Vehicle } from "@/types";

// Cada círculo ocupa 256×256 no atlas de textura da GPU — estourar o atlas
// é o que deixava ícones pretos no Android (ver getIcon em VehicleLayer).
// Passou do teto, os demais ficam com o ícone 3D.
const MAX_PHOTOS = 60;

export const wantsPhotoMarker = (v: Vehicle) => v.mapIconStyle === "photo";

export function usePhotoMarkers(
  vehicles: Vehicle[],
  colorReady: boolean,
  baseIconUrl: (v: Vehicle) => string,
  zoom: number,
  map?: google.maps.Map | null,
) {
  // Tamanho do círculo acompanha o zoom (pequeno de longe, 84px de perto).
  // Bug 2026-10-01 ("fica grande do nada quando tiro ou boto zoom"): o
  // `zoom` dos chamadores só atualiza no "idle" (fim do movimento) — o
  // círculo mantinha o tamanho antigo durante o zoom inteiro e pulava de
  // uma vez no final (ex.: 40px → 84px). Agora segue o zoom AO VIVO
  // (zoom_changed); o estado só muda quando o tamanho arredondado muda.
  const [photoSize, setPhotoSize] = useState(() => photoMarkerSizeForZoom(zoom));
  useEffect(() => {
    setPhotoSize(photoMarkerSizeForZoom(map?.getZoom() ?? zoom));
    if (!map) return;
    const listener = map.addListener("zoom_changed", () => {
      const z = map.getZoom();
      if (z != null) setPhotoSize(photoMarkerSizeForZoom(z));
    });
    return () => listener.remove();
  }, [map, zoom]);
  // Só os ids (ordenados) entram na chave — o deslize do ícone gera arrays
  // novos a cada tick sem mudar quem está na tela.
  const idsKey = vehicles
    .filter(wantsPhotoMarker)
    .slice(0, MAX_PHOTOS)
    .map((v) => v.id)
    .sort()
    .join(",");
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);
  const allowed = useMemo(() => new Set(ids), [ids]);
  const { data: batch, isFetched: batchFetched } = useVehicleImages(ids, ids.length > 0);
  // Acumula entre movimentos do mapa pra foto não piscar.
  const knownRef = useRef<Record<string, string>>({});
  const known = useMemo(() => {
    if (batch) {
      for (const [id, urls] of Object.entries(batch)) {
        const u = urls["256"] ?? urls["128"] ?? urls.original;
        if (u) knownRef.current[id] = resolveGpsDriveUrl(u);
      }
    }
    return { ...knownRef.current };
  }, [batch]);
  // Círculos são gerados em segundo plano; quando um fica pronto, redesenha.
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  const ringFor = useCallback((v: Vehicle) => (colorReady ? resolveAutoColor(v) : AUTO_PREVIEW_COLOR), [colorReady]);

  // Data URI do círculo, ou null (veículo não usa foto / ainda gerando) —
  // aí o chamador usa o ícone normal.
  const circleFor = useCallback(
    (v: Vehicle): string | null => {
      if (!allowed.has(v.id)) return null;
      const ring = ringFor(v);
      const photo = known[v.id];
      // Lista de fotos ainda não respondeu pra este veículo: círculo vazio
      // em vez do ícone 3D dentro (que depois trocava pra foto).
      if (!photo && !batchFetched) return placeholderCircleIcon(ring);
      const usePhoto = !!photo && !photoFailed(photo, ring);
      return photoCircleIcon(usePhoto ? photo : baseIconUrl(v), ring, !usePhoto, bump);
    },
    // tick: força identidade nova quando um círculo fica pronto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowed, known, ringFor, baseIconUrl, bump, tick, batchFetched],
  );

  // Seta de direção em volta do círculo, só pra quem está andando.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headingLayer = (Mod: any, id: string, data: Vehicle[], mapHeading = 0) => {
    const moving = data.filter((v) => v.moving && allowed.has(v.id));
    if (moving.length === 0) return [];
    return [
      new Mod.IconLayer({
        id,
        data: moving,
        pickable: false,
        sizeUnits: "pixels",
        getPosition: (v: Vehicle) => [v.lng, v.lat],
        getSize: photoSize * ARROW_SCALE,
        getAngle: (v: Vehicle) => 360 - ((v.bearing ?? 0) - mapHeading),
        getIcon: (v: Vehicle) => {
          const color = ringFor(v);
          return {
            id: `arrow-${color}`,
            url: headingArrowIcon(color),
            width: PHOTO_MARKER_PX,
            height: PHOTO_MARKER_PX,
            anchorY: PHOTO_MARKER_PX / 2,
          };
        },
        updateTriggers: { getIcon: [colorReady], getAngle: [mapHeading] },
      }),
    ];
  };

  return { circleFor, headingLayer, photoSize };
}

// Tamanho/ícone do IconLayer principal com o círculo, pra os dois mapas.
export function photoIconProps(circle: string | null) {
  return circle
    ? { url: circle, width: PHOTO_MARKER_PX, height: PHOTO_MARKER_PX, anchorY: PHOTO_MARKER_PX / 2 }
    : null;
}
export { PHOTO_MARKER_SIZE };
